import type { HeadFit, ImageCrop } from './types.js';
import type { FaceBox } from './faces.js';

/**
 * Mini Me head placement: measuring a cut-out head and fitting it to its slot.
 *
 * A face cut-out (cutout.pro's Face Cutout, or the local fallback) comes back
 * the SAME size as the uploaded photo, transparent everywhere but the head(s).
 * The photo's own framing therefore says nothing about where the head is or
 * how big it is — a selfie head fills half the picture, a head in a holiday
 * snap is a speck. Framing on the face detector's box alone (`cropForFace`)
 * guesses the hair and the chin and gets a different size for every photo.
 *
 * Measured on real cutout.pro output (see docs/mini-me-head-placement.md):
 * the cut stops at the chin, keeps every head in a group photo, and the hair
 * can be much wider than the face. So the head is measured from the cut's own
 * alpha — the connected shape around the chosen face, hair top to chin — and
 * the face box only decides WHICH shape, where its centre line is, and how
 * far the head leans.
 *
 * Pure: the server measures (it has the pixels), the widget and the studio
 * frame (they know the slot), and all three share this file so the framing a
 * designer previews is the framing the shopper gets.
 */

/** A rectangle in fractions of the picture. */
export interface FractionBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A head slot's visible window (fractions of the slot) with its outline: the
 * visible span across it at `HEAD_PROFILE_ROWS` evenly spaced rows, as
 * `[left, right]` fractions of the window's own width (a row whose centre is
 * hidden is `[0.5, 0.5]`).
 */
export interface HeadWindow extends FractionBox {
  profile?: Array<[number, number]>;
}

/** Rows in a window's measured outline: finer than a face's curve needs. */
export const HEAD_PROFILE_ROWS = 24;

/** One measured head, in fractions of the cut-out (= the original photo). */
export interface HeadMeasure {
  /** The head's silhouette: hair top to chin, widest hair to widest hair. */
  box: FractionBox;
  /** The face the head was matched to, when a detector found one. */
  face?: FractionBox;
  /**
   * The vertical line the head is centred on: the face's centre when there is
   * a face (a side parting must not push the face off-centre), otherwise the
   * silhouette's.
   */
  centerX: number;
  /** Degrees the head leans (eye line, clockwise positive); 0 when unknown. */
  roll: number;
  /** Share of the picture's pixels the head covers — a speck is worth a warning. */
  coverage: number;
}

/** The detector's face, plus the eye points when it reports them. */
export interface MeasuredFace extends FaceBox {
  /** Two eye centres in fractions of the picture, in any order. */
  eyes?: Array<{ x: number; y: number }>;
}

/** An alpha plane, row-major, one byte per pixel (0 = transparent). */
export interface AlphaPlane {
  width: number;
  height: number;
  data: ArrayLike<number>;
}

/** What placement does when neither the slot nor the shop says otherwise. */
export const DEFAULT_HEAD_FIT: Required<Omit<HeadFit, 'off' | 'area'>> = {
  fill: 'height',
  size: 1,
  align: 'center',
  offsetX: 0,
  offsetY: 0,
  trim: 0.5,
};

/**
 * The most hair a fit may leave outside the window, of the head's height. At
 * 1 the face alone decides; 0.5 (the default) keeps a little more of a big
 * head of hair for nearly the same face size (evaluated on real cut-outs on
 * the 45 archived Mini Me bodies: face 85 % vs 88 % of the window's height,
 * against 63 % when the whole head had to fit).
 */
export const HEAD_TRIM_MAX = 1;
/** Off each side, the hair may lose this share of what the crown may. */
const SIDE_TRIM = 0.8;
/**
 * The face core that never leaves the window, around the detector's face
 * box: a little forehead above it (the box starts at the brows on some
 * faces), a little cheek to each side, and down to the chin the cut ends at.
 */
const CORE_FOREHEAD = 0.1;
const CORE_CHEEK = 0.06;

/** Alpha at or above this is head; soft hair edges below it are not measured. */
const SOLID = 96;
/** A shape smaller than this share of the biggest one is a stray fleck. */
const FLECK = 0.02;
/** Leans under this are noise in the eye points; over the cap, a pose, not a tilt. */
const ROLL_MIN = 1.5;
const ROLL_MAX = 30;

/**
 * Finds the heads in a face cut-out's alpha.
 *
 * One entry per detected face that has a head under it (left to right, the
 * order the face picker shows), or — when no face was detected — one per
 * significant shape, largest first. Empty when the cut is empty.
 */
export function measureHeads(alpha: AlphaPlane, faces: MeasuredFace[] = []): HeadMeasure[] {
  const { width: W, height: H } = alpha;
  if (W <= 0 || H <= 0) return [];
  const labels = new Int32Array(W * H);
  const shapes: Array<{ area: number; minX: number; minY: number; maxX: number; maxY: number }> = [];
  // Connected shapes (4-connected) of solid pixels, by an explicit stack flood.
  const stack = new Int32Array(W * H);
  for (let start = 0; start < W * H; start++) {
    if (labels[start] !== 0 || (alpha.data[start] ?? 0) < SOLID) continue;
    const id = shapes.length + 1;
    const shape = { area: 0, minX: W, minY: H, maxX: -1, maxY: -1 };
    let top = 0;
    stack[top++] = start;
    labels[start] = id;
    while (top > 0) {
      const p = stack[--top]!;
      const x = p % W;
      const y = (p - x) / W;
      shape.area++;
      if (x < shape.minX) shape.minX = x;
      if (x > shape.maxX) shape.maxX = x;
      if (y < shape.minY) shape.minY = y;
      if (y > shape.maxY) shape.maxY = y;
      const push = (q: number): void => {
        if (labels[q] === 0 && (alpha.data[q] ?? 0) >= SOLID) {
          labels[q] = id;
          stack[top++] = q;
        }
      };
      if (x > 0) push(p - 1);
      if (x < W - 1) push(p + 1);
      if (y > 0) push(p - W);
      if (y < H - 1) push(p + W);
    }
    shapes.push(shape);
  }
  if (shapes.length === 0) return [];
  const biggest = Math.max(...shapes.map((s) => s.area));
  const significant = shapes
    .map((s, i) => ({ ...s, id: i + 1 }))
    .filter((s) => s.area >= biggest * FLECK);

  const toBox = (s: { minX: number; minY: number; maxX: number; maxY: number }): FractionBox => ({
    x: s.minX / W,
    y: s.minY / H,
    w: (s.maxX - s.minX + 1) / W,
    h: (s.maxY - s.minY + 1) / H,
  });

  if (faces.length === 0) {
    return significant
      .sort((a, b) => b.area - a.area)
      .map((s) => {
        const box = toBox(s);
        return { box, centerX: box.x + box.w / 2, roll: 0, coverage: s.area / (W * H) };
      });
  }

  // Which shape each face's head is: the one with the most solid pixels inside
  // the face box. Too few votes means the "head" is a lock of someone else's
  // hair (or the face's cut came back empty), and the face has no head.
  const ordered = [...faces].sort((a, b) => a.x - b.x);
  const owner = ordered.map((face) => {
    const fx0 = Math.max(0, Math.floor(face.x * W)), fx1 = Math.min(W - 1, Math.ceil((face.x + face.w) * W));
    const fy0 = Math.max(0, Math.floor(face.y * H)), fy1 = Math.min(H - 1, Math.ceil((face.y + face.h) * H));
    const votes = new Map<number, number>();
    for (let y = fy0; y <= fy1; y++) {
      for (let x = fx0; x <= fx1; x++) {
        const id = labels[y * W + x]!;
        if (id) votes.set(id, (votes.get(id) ?? 0) + 1);
      }
    }
    let best = 0;
    let bestVotes = 0;
    for (const [id, n] of votes) if (n > bestVotes) { best = id; bestVotes = n; }
    const shape = significant.find((s) => s.id === best);
    return shape && bestVotes >= (fx1 - fx0 + 1) * (fy1 - fy0 + 1) * 0.15 ? shape.id : 0;
  });

  // Touching heads (cheek to cheek, hair over a shoulder) are ONE connected
  // shape. Every face that landed on it used to measure the pair as its head,
  // and the shopper's pick kept both people. The shape is cut between them:
  // at the dip between the two crowns where there is one, else on the line
  // between the faces weighted by their size.
  const splitOff = new Set<number>();
  const byShape = new Map<number, number[]>();
  owner.forEach((id, i) => {
    if (id) byShape.set(id, [...(byShape.get(id) ?? []), i]);
  });
  for (const [id, members] of byShape) {
    const shape = significant.find((s) => s.id === id)!;
    const facesPx = members.map((i) => facePx(ordered[i]!, W, H));
    if (members.length >= 2) {
      const parts = splitShape(labels, W, shape, facesPx, shapes);
      members.forEach((i, k) => (owner[i] = parts[k]!));
    } else {
      // One face on a shape much wider than a head: a second head the
      // detector missed (a profile, a turned or half-hidden face) may be
      // leaning on it. It is cut off at the dip between the crowns and
      // offered as a head of its own.
      const extra = splitMissedHead(labels, W, shape, facesPx[0]!, shapes);
      if (extra) {
        owner[members[0]!] = extra.kept;
        for (const part of extra.others) splitOff.add(part);
      }
    }
  }
  const parts = shapes.map((s, i) => ({ ...s, id: i + 1 })).filter((s) => s.area > 0 && s.area >= biggest * FLECK);

  const heads: HeadMeasure[] = [];
  const claimed = new Set<number>();
  ordered.forEach((face, i) => {
    const shape = parts.find((s) => s.id === owner[i]);
    if (!shape) return;
    claimed.add(shape.id);
    let box = toBox(shape);
    // cutout.pro stops at the chin (measured: within a third of a face height
    // of the detector's box bottom). A cut that runs well past it — the local
    // fallback keeps the neck and shoulders — is trimmed just under the chin,
    // and its width re-read over the head's own rows, so the shoulders do not
    // count as head.
    const faceBottom = (face.y + face.h) * H;
    if (shape.maxY > faceBottom + face.h * H * 0.35) {
      const chinRow = Math.min(shape.maxY, Math.round(faceBottom + face.h * H * 0.08));
      let minX = W;
      let maxX = -1;
      for (let y = shape.minY; y <= chinRow; y++) {
        for (let x = shape.minX; x <= shape.maxX; x++) {
          if (labels[y * W + x] === shape.id) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
          }
        }
      }
      if (maxX >= minX) box = toBox({ minX, minY: shape.minY, maxX, maxY: chinRow });
    }
    // A shape still far wider than a head (a row of people pressed together
    // that the cut above could not part): keep the silhouette's height, but
    // only the width around this face that a head plausibly has.
    const faceBox: FractionBox = { x: face.x, y: face.y, w: face.w, h: face.h };
    const centerX = face.x + face.w / 2;
    if (box.w > face.w * 3.2) {
      const half = face.w * 1.3;
      const left = Math.max(box.x, centerX - half);
      const right = Math.min(box.x + box.w, centerX + half);
      box.x = left;
      box.w = right - left;
    }
    heads.push({ box, face: faceBox, centerX, roll: rollOf(face), coverage: shape.area / (W * H) });
  });
  // A cut whose faces the detector missed still has heads; list them after.
  // A head cut off a shared shape above is one of those, whatever the count.
  const smallest = Math.min(...heads.map((h) => h.coverage * W * H));
  for (const s of parts.sort((a, b) => b.area - a.area)) {
    if (claimed.has(s.id)) continue;
    const headLike = splitOff.has(s.id) && s.area >= smallest * 0.3;
    if (!headLike && heads.length >= Math.max(faces.length, 1)) continue;
    const box = toBox(s);
    heads.push({ box, centerX: box.x + box.w / 2, roll: 0, coverage: s.area / (W * H) });
  }
  return heads;
}

/** A shape's pixel statistics, as the labelling records them. */
interface ShapeStats {
  area: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** A face in the alpha plane's pixels: centre and size. */
interface FacePx {
  cx: number;
  cy: number;
  w: number;
  h: number;
}

function facePx(face: FaceBox, W: number, H: number): FacePx {
  return { cx: (face.x + face.w / 2) * W, cy: (face.y + face.h / 2) * H, w: face.w * W, h: face.h * H };
}

/**
 * The first row the shape reaches in each column from `x0` to `x1` — its
 * crown line, lightly smoothed. A column the shape does not reach reads as
 * one row under its bottom: a gap is the deepest dip there is.
 */
function crownLine(labels: Int32Array, W: number, id: number, shape: ShapeStats, x0: number, x1: number, radius: number): Float64Array {
  const raw = new Float64Array(x1 - x0 + 1);
  for (let x = x0; x <= x1; x++) {
    let top = shape.maxY + 1;
    for (let y = shape.minY; y <= shape.maxY; y++) {
      if (labels[y * W + x] === id) {
        top = y;
        break;
      }
    }
    raw[x - x0] = top;
  }
  const out = new Float64Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    let sum = 0;
    let n = 0;
    for (let j = Math.max(0, i - radius); j <= Math.min(raw.length - 1, i + radius); j++) {
      sum += raw[j]!;
      n++;
    }
    out[i] = sum / n;
  }
  return out;
}

/**
 * The dip between two crowns in a crown line starting at column `x0`: the
 * column where the line is lowest, provided it rises again by `depth` rows on
 * BOTH sides. Undefined when the line is a dome or a slope — one head, or two
 * at different heights with no valley to cut along. Ties go to the column
 * nearest `prefer`.
 */
function crownDip(line: Float64Array, x0: number, depth: number, prefer: number): number | undefined {
  if (line.length < 3) return undefined;
  let valley = 0;
  for (let i = 1; i < line.length; i++) {
    const better = line[i]! > line[valley]! + 1e-9;
    const tie = Math.abs(line[i]! - line[valley]!) <= 1e-9 && Math.abs(x0 + i - prefer) < Math.abs(x0 + valley - prefer);
    if (better || tie) valley = i;
  }
  let left = Infinity;
  for (let i = 0; i <= valley; i++) left = Math.min(left, line[i]!);
  let right = Infinity;
  for (let i = valley; i < line.length; i++) right = Math.min(right, line[i]!);
  return line[valley]! - Math.max(left, right) >= depth ? x0 + valley : undefined;
}

/** Moves every pixel of shape `id` into one new shape per part; returns the parts' ids. */
function relabel(
  labels: Int32Array,
  W: number,
  id: number,
  shape: ShapeStats,
  shapes: ShapeStats[],
  count: number,
  partOf: (x: number, y: number) => number,
): number[] {
  const ids: number[] = [];
  for (let k = 0; k < count; k++) {
    shapes.push({ area: 0, minX: W, minY: Number.MAX_SAFE_INTEGER, maxX: -1, maxY: -1 });
    ids.push(shapes.length);
  }
  for (let y = shape.minY; y <= shape.maxY; y++) {
    for (let x = shape.minX; x <= shape.maxX; x++) {
      const p = y * W + x;
      if (labels[p] !== id) continue;
      const to = ids[partOf(x, y)]!;
      labels[p] = to;
      const s = shapes[to - 1]!;
      s.area++;
      if (x < s.minX) s.minX = x;
      if (x > s.maxX) s.maxX = x;
      if (y < s.minY) s.minY = y;
      if (y > s.maxY) s.maxY = y;
    }
  }
  // The shared shape is gone: its pixels all belong to the parts now.
  shapes[id - 1]!.area = 0;
  return ids;
}

/**
 * Cuts one shape shared by several faces into one part per face (returned in
 * the faces' order). Faces side by side are parted by vertical cuts — at the
 * dip between their crowns when the outline has one, else on the line between
 * them weighted by face width (a bigger face gets the wider share). Faces
 * stacked above one another go to the nearest face instead.
 */
function splitShape(labels: Int32Array, W: number, shape: ShapeStats & { id: number }, faces: FacePx[], shapes: ShapeStats[]): number[] {
  const order = faces.map((_, i) => i).sort((a, b) => faces[a]!.cx - faces[b]!.cx);
  const sideBySide = order.every((i, k) => {
    if (k === 0) return true;
    const a = faces[order[k - 1]!]!;
    const b = faces[i]!;
    return b.cx - a.cx >= 0.6 * Math.min(a.w, b.w);
  });
  if (!sideBySide) {
    return relabel(labels, W, shape.id, shape, shapes, faces.length, (x, y) => {
      let best = 0;
      let bestD = Infinity;
      faces.forEach((f, i) => {
        const d = ((x - f.cx) / f.w) ** 2 + ((y - f.cy) / (1.6 * f.h)) ** 2;
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    });
  }
  const cuts: number[] = [];
  for (let k = 1; k < order.length; k++) {
    const a = faces[order[k - 1]!]!;
    const b = faces[order[k]!]!;
    const middle = a.cx + ((b.cx - a.cx) * a.w) / (a.w + b.w);
    const x0 = Math.max(shape.minX, Math.round(a.cx + 0.25 * a.w));
    const x1 = Math.min(shape.maxX, Math.round(b.cx - 0.25 * b.w));
    let cut = middle;
    if (x1 - x0 >= 2) {
      const radius = Math.max(1, Math.round(0.04 * (a.w + b.w)));
      const dip = crownDip(crownLine(labels, W, shape.id, shape, x0, x1, radius), x0, 0.12 * ((a.h + b.h) / 2), middle);
      if (dip !== undefined) cut = dip;
    }
    cuts.push(cut);
  }
  const strips = relabel(labels, W, shape.id, shape, shapes, faces.length, (x) => {
    let k = 0;
    while (k < cuts.length && x >= cuts[k]!) k++;
    return k;
  });
  // Strip k belongs to the k-th face from the left.
  const out: number[] = new Array<number>(faces.length);
  order.forEach((faceIndex, k) => (out[faceIndex] = strips[k]!));
  return out;
}

/**
 * A second head leaning on the one a face was found on, missed by the
 * detector. Looked for only where the shape runs on well past what one head
 * of hair spans, and only across a real dip between two crowns — a big head
 * of hair is a dome, never a valley. Returns the face's own part and the parts
 * cut off, or undefined when the shape is one head.
 */
function splitMissedHead(
  labels: Int32Array,
  W: number,
  shape: ShapeStats & { id: number },
  face: FacePx,
  shapes: ShapeStats[],
): { kept: number; others: number[] } | undefined {
  const reach = 1.9 * face.w;
  const radius = Math.max(1, Math.round(0.04 * face.w));
  const depth = 0.2 * face.h;
  const minWidth = 0.6 * face.w;
  let right: number | undefined;
  if (shape.maxX - face.cx > reach) {
    const x0 = Math.round(face.cx + 0.5 * face.w);
    const x1 = Math.round(shape.maxX - minWidth);
    if (x1 - x0 >= 2) right = crownDip(crownLine(labels, W, shape.id, shape, x0, x1, radius), x0, depth, x0);
  }
  let left: number | undefined;
  if (face.cx - shape.minX > reach) {
    const x0 = Math.round(shape.minX + minWidth);
    const x1 = Math.round(face.cx - 0.5 * face.w);
    if (x1 - x0 >= 2) left = crownDip(crownLine(labels, W, shape.id, shape, x0, x1, radius), x0, depth, x1);
  }
  if (left === undefined && right === undefined) return undefined;
  const ids = relabel(labels, W, shape.id, shape, shapes, 3, (x) =>
    left !== undefined && x < left ? 0 : right !== undefined && x >= right ? 2 : 1,
  );
  const others = [left !== undefined ? ids[0]! : 0, right !== undefined ? ids[2]! : 0].filter((id) => id > 0);
  return { kept: ids[1]!, others };
}

/** The eye line's lean in degrees (clockwise positive, y down), or 0. */
function rollOf(face: MeasuredFace): number {
  const eyes = face.eyes;
  if (!eyes || eyes.length < 2) return 0;
  const [a, b] = eyes[0]!.x <= eyes[1]!.x ? [eyes[0]!, eyes[1]!] : [eyes[1]!, eyes[0]!];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (!(dx > 0)) return 0;
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return Number.isFinite(deg) ? deg : 0;
}

/**
 * The part of a head slot the shopper will actually SEE.
 *
 * A Mini Me slot is rarely all visible: the body is drawn on top of it (the
 * collar, a helmet rim, a hood) and the slot's own mask trims it further.
 * Centring the head in the raw slot hides the chin and mouth behind the
 * collar — measured on the astronaut keychain, a third of the slot is under
 * the suit. So the head is fitted into the visible window instead.
 *
 * Takes alpha planes over the slot's box, all the same size: the slot's mask
 * (what the photo may show through), everything drawn above the slot, and
 * everything drawn below it (the product's own silhouette).
 * Returns the window in fractions of the slot — the run of visible rows down
 * the slot's centre line (where the face goes), and the visible width across
 * the upper middle of that run (cheeks and hair) — or undefined when nearly
 * the whole slot shows, in which case the slot itself is the window.
 */
export function headWindow(mask: AlphaPlane, above?: AlphaPlane, below?: AlphaPlane): HeadWindow | undefined {
  const { width: W, height: H } = mask;
  if (W < 8 || H < 8) return undefined;
  // What is under the slot is the product itself — a wooden keychain is cut
  // along its drawn silhouette — so a head is only visible where there is
  // product beneath it. No layers below means the slot stands on its own.
  // Some templates draw the head's placeholder in the SLOT's mask and only
  // the body below it; there the layers below say nothing about the head, so
  // they are trusted only when they keep most of what would otherwise show.
  const open = (i: number): boolean => (mask.data[i] ?? 0) >= 128 && (above ? (above.data[i] ?? 0) < 128 : true);
  let openCount = 0;
  let keptCount = 0;
  for (let i = 0; i < W * H; i++) {
    if (!open(i)) continue;
    openCount++;
    if (below && (below.data[i] ?? 0) >= 128) keptCount++;
  }
  if (openCount === 0) return undefined;
  const under = below && keptCount >= openCount * 0.6 ? below : undefined;
  const seen = (x: number, y: number): boolean => {
    const i = y * W + x;
    return open(i) && (under ? (under.data[i] ?? 0) >= 128 : true);
  };
  let visibleCount = 0;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (seen(x, y)) visibleCount++;
  if (visibleCount === 0) return undefined;

  // Down the centre line: a band a fifth of the slot wide, so one stray
  // pixel of a collar's stitching does not end the window.
  const c = Math.floor(W / 2);
  const band = Math.max(1, Math.round(W * 0.1));
  const rowOpen: boolean[] = [];
  for (let y = 0; y < H; y++) {
    let open = 0;
    let total = 0;
    for (let x = Math.max(0, c - band); x <= Math.min(W - 1, c + band); x++) {
      total++;
      if (seen(x, y)) open++;
    }
    rowOpen.push(open >= total * 0.5);
  }
  const runs: Array<{ top: number; end: number }> = [];
  for (let y = 0; y < H; ) {
    if (!rowOpen[y]) { y++; continue; }
    let end = y;
    while (end + 1 < H && rowOpen[end + 1]) end++;
    runs.push({ top: y, end });
    y = end + 1;
  }
  if (runs.length === 0) return undefined;
  // The head's own outline, ignoring what is drawn over it — for rows an
  // accessory crosses (below).
  const shaped = (x: number, y: number): boolean => {
    const i = y * W + x;
    return (mask.data[i] ?? 0) >= 128 && (under ? (under.data[i] ?? 0) >= 128 : true);
  };
  const spanWith = (y: number, test: (x: number, y: number) => boolean): [number, number] | undefined => {
    if (!test(c, y)) return undefined;
    let l = c;
    while (l > 0 && test(l - 1, y)) l--;
    let r = c;
    while (r < W - 1 && test(r + 1, y)) r++;
    return [l, r];
  };
  const medianWidth = (from: number, to: number): number => {
    const w: number[] = [];
    for (let y = from; y <= to; y++) {
      const span = spanWith(y, seen);
      if (span) w.push(span[1] - span[0] + 1);
    }
    return w.length ? [...w].sort((a, b) => a - b)[Math.floor(w.length / 2)]! : 0;
  };
  // An accessory drawn ACROSS the face — goggles, glasses, a headband —
  // splits the visible head into a run above it and a run below it. The face
  // belongs behind it, filling the head, as on the shop's own Naučnik photo;
  // taking the longer run alone squeezed the whole face under the goggles.
  // A band is bridged when head shows on both sides of it at a similar width
  // and it is thin; a cap on top (nothing of the head above it) and a collar
  // below (nothing of the head under it, or only a narrow neckline) are not.
  const bridged: Array<{ top: number; end: number; bands: Array<[number, number]> }> = [];
  for (const run of runs) {
    const last = bridged[bridged.length - 1];
    if (last) {
      const gap = run.top - last.end - 1;
      const above = medianWidth(last.top, last.end);
      const below = medianWidth(run.top, run.end);
      const substantial = last.end - last.top + 1 >= H * 0.1 && run.end - run.top + 1 >= H * 0.15;
      const alike = above > 0 && below > 0 && Math.min(above, below) >= Math.max(above, below) * 0.6;
      // The rows under the band still belong to the head's outline.
      let outlined = true;
      for (let y = last.end + 1; y < run.top && outlined; y++) outlined = shaped(c, y);
      if (gap <= H * 0.3 && substantial && alike && outlined) {
        // The accessory's reach: the covered rows, and as many again below
        // them — goggles leave only the nose notch visible under the lenses.
        last.bands.push([last.end + 1, Math.min(run.end, run.top + gap)]);
        last.end = run.end;
        continue;
      }
    }
    bridged.push({ ...run, bands: [] });
  }
  let best = bridged[0]!;
  for (const run of bridged) if (run.end - run.top > best.end - best.top) best = run;
  const bestLen = best.end - best.top + 1;
  if (bestLen < H * 0.15) return undefined;
  const top = best.top;
  // The visible width through the centre, row by row. Where an accessory
  // crosses the face (a bridged run), the head's own outline stands in for
  // the rows it covers or pinches — goggles see-through in the lenses leave
  // only the nose notch visible under their frame — except in the run's
  // lowest quarter, where a collar's lapels really do bound the chin.
  const accessoryRows = best.bands.length > 0 ? best.top + Math.round((best.end - best.top + 1) * 0.75) : -1;
  const spanAt = (y: number): [number, number] | undefined => {
    const own = spanWith(y, seen);
    if (y < best.top || y > accessoryRows) return own;
    const outline = spanWith(y, shaped);
    if (!outline) return own;
    if (!own || own[1] - own[0] + 1 < (outline[1] - outline[0] + 1) * 0.7) return outline;
    return own;
  };
  const widths: number[] = [];
  for (let y = top; y < top + bestLen; y++) {
    const span = spanAt(y);
    widths.push(span ? span[1] - span[0] + 1 : 0);
  }
  // A bust-shaped slot (a round head, then the shoulders flaring out — the
  // migrated Mini Me masks are bells) is for the head only down to where the
  // flare starts: below it are the neck and the shirt. The head is as wide
  // as the widest row of the run's upper part; the first row below that
  // grows clearly past it is the shoulder line. (An oval that merely keeps
  // widening towards its middle is not a flare.)
  let bottom = top + bestLen - 1;
  const upperRows = widths.slice(0, Math.round(bestLen * 0.6) + 1);
  if (upperRows.length) {
    const base = Math.max(...upperRows);
    for (let i = Math.round(bestLen * 0.6); i < bestLen; i++) {
      if (widths[i]! > base * 1.08) {
        bottom = top + i;
        break;
      }
    }
  }
  const runLen = bottom - top + 1;
  if (visibleCount >= W * H * 0.92 && runLen >= bestLen) return undefined;

  // Across: the visible span through the centre, over the upper middle of
  // the run — where the cheeks and the hair are — median-smoothed.
  const lefts: number[] = [];
  const rights: number[] = [];
  const from = Math.round(top + runLen * 0.2);
  const to = Math.round(top + runLen * 0.65);
  for (let y = from; y <= to; y++) {
    const span = spanAt(y);
    if (!span) continue;
    lefts.push(span[0]);
    rights.push(span[1]);
  }
  if (lefts.length === 0) return undefined;
  const median = (v: number[]): number => [...v].sort((a, b) => a - b)[Math.floor(v.length / 2)]!;
  const left = median(lefts);
  const right = median(rights);
  const width = right - left + 1;
  // The outline, row by row: how far the head may reach at each height.
  const profile: Array<[number, number]> = [];
  for (let i = 0; i < HEAD_PROFILE_ROWS; i++) {
    const y = Math.min(H - 1, Math.floor(top + ((i + 0.5) / HEAD_PROFILE_ROWS) * runLen));
    const span = spanAt(y);
    const r4 = (v: number): number => Math.round(v * 10000) / 10000;
    profile.push(span ? [r4((span[0] - left) / width), r4((span[1] + 1 - left) / width)] : [0.5, 0.5]);
  }
  return { x: left / W, y: top / H, w: width / W, h: runLen / H, profile };
}

/** Placement with every gap filled from the shop's default, then the built-in one. */
export function resolveHeadFit(own?: HeadFit, shop?: HeadFit): Required<Omit<HeadFit, 'off' | 'area'>> & { off: boolean } {
  const pick = <K extends keyof HeadFit>(key: K): HeadFit[K] => own?.[key] ?? shop?.[key];
  const num = (value: unknown, fallback: number, min: number, max: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
  const align = pick('align');
  const fill = pick('fill');
  return {
    fill: fill === 'height' || fill === 'face' ? fill : DEFAULT_HEAD_FIT.fill,
    size: num(pick('size'), DEFAULT_HEAD_FIT.size, 0.4, 1.6),
    align: align === 'top' || align === 'bottom' || align === 'center' ? align : DEFAULT_HEAD_FIT.align,
    offsetX: num(pick('offsetX'), 0, -0.5, 0.5),
    offsetY: num(pick('offsetY'), 0, -0.5, 0.5),
    trim: num(pick('trim'), DEFAULT_HEAD_FIT.trim, 0, HEAD_TRIM_MAX),
    off: own?.off === true,
  };
}

/**
 * Where the head lands in the slot, in slot pixels from its top-left corner:
 * the head box as it will be drawn. Exported so the studio can draw the guide
 * and the storefront can aim its animation at the same rectangle.
 */
export function headPlacement(
  head: HeadMeasure,
  picture: { w: number; h: number },
  slot: { w: number; h: number },
  fit?: HeadFit,
): { x: number; y: number; w: number; h: number; centerX: number; scale: number } {
  const rules = resolveHeadFit(fit);
  // The window the head is fitted to: the slot's visible part when known.
  const area = fit?.area && fit.area.w > 0 && fit.area.h > 0 ? fit.area : { x: 0, y: 0, w: 1, h: 1 };
  const win = { x: area.x * slot.w, y: area.y * slot.h, w: area.w * slot.w, h: area.h * slot.h };
  const box = head.box;
  const px = { x: box.x * picture.w, y: box.y * picture.h, w: box.w * picture.w, h: box.h * picture.h };
  const cx = head.centerX * picture.w;
  const centreX = win.x + win.w / 2 + rules.offsetX * win.w;
  if (rules.fill === 'height') {
    // Hair top to chin exactly as tall as the area, centred on the face; the
    // size setting scales from there and the alignment applies either way (a
    // bigger head centred loses as much crown as chin). The shopper zooms on.
    const scale = (rules.size * win.h) / Math.max(px.h, 1e-6);
    const free = win.h - px.h * scale;
    return {
      x: centreX - (cx - px.x) * scale,
      y: win.y + (rules.align === 'top' ? 0 : rules.align === 'bottom' ? free : free / 2) + rules.offsetY * win.h,
      w: px.w * scale,
      h: px.h * scale,
      centerX: centreX,
      scale,
    };
  }
  // Where the head's top edge lands at a scale. A head taller than its window
  // loses its crown, never its chin: the chin sits on the window's bottom edge
  // (the collar line) and the hair goes out of the top. With room to spare,
  // the alignment decides.
  const topAt = (scale: number): number => {
    const free = win.h - px.h * scale;
    return win.y + (free < 0 || rules.align === 'bottom' ? free : rules.align === 'top' ? 0 : free / 2) + rules.offsetY * win.h;
  };
  const fitted = headScale(head, px, cx, win, rules.trim, (area as { profile?: Array<[number, number]> }).profile, centreX, topAt);
  // A smaller size shrinks the fitted head; a larger one grows it only as far
  // as the face still clears the window's outline — "bigger" never cuts a face.
  const scale = rules.size <= 1 ? rules.size * fitted.scale : Math.max(fitted.scale, Math.min(rules.size * fitted.scale, fitted.faceMax));
  return {
    x: centreX - (cx - px.x) * scale,
    y: topAt(scale),
    w: px.w * scale,
    h: px.h * scale,
    centerX: centreX,
    scale,
  };
}

/**
 * How large a head may be drawn in its window (window pixels per picture
 * pixel, before the size setting).
 *
 * The whole head — hair top to chin, the wider side of the hair about the
 * face's centre line — always fits: that was the whole rule until September
 * 2026, and voluminous hair made the face small (the head box can be twice the
 * face's width). BearBox's own Mini Me keychains show the face filling the
 * head shape, hair trimmed by its outline. So the head grows past the whole
 * fit, letting the hair (and only the hair) leave the window, until either
 *  - the face would touch the window's outline: the face is the ellipse inside
 *    its core (the detector's box, a little forehead above and cheek to each
 *    side, down to the chin the cut ends at), checked row by row against the
 *    measured outline — a bell or a helmet opening, not its bounding box —
 *    and against the bounding box when no outline was measured; or
 *  - the hair would lose more than `trim` of the head's height at the crown
 *    (`SIDE_TRIM` of that off each side).
 * A head whose hair hugs its face has nothing to trim and keeps its size;
 * without a detected face there is no face to protect and the whole head fits.
 */
function headScale(
  head: HeadMeasure,
  px: { x: number; y: number; w: number; h: number },
  cx: number,
  win: { x: number; y: number; w: number; h: number },
  trim: number,
  profile: Array<[number, number]> | undefined,
  centreX: number,
  topAt: (scale: number) => number,
): { scale: number; faceMax: number } {
  const halfW = Math.max(cx - px.x, px.x + px.w - cx, 1e-6);
  const whole = Math.min(win.w / (2 * halfW), win.h / Math.max(px.h, 1e-6));
  const face = head.face;
  // No face to protect: the whole head fits, and a size setting scales it freely.
  if (!face) return { scale: whole, faceMax: Infinity };
  const picW = px.w / Math.max(head.box.w, 1e-9);
  const picH = px.h / Math.max(head.box.h, 1e-9);
  // The face core in picture pixels, never wider or taller than the head.
  const f = { x: face.x * picW, y: face.y * picH, w: face.w * picW, h: face.h * picH };
  const x0 = Math.max(px.x, f.x - f.w * CORE_CHEEK);
  const x1 = Math.min(px.x + px.w, f.x + f.w * (1 + CORE_CHEEK));
  const y0 = Math.max(px.y, f.y - f.h * CORE_FOREHEAD);
  const chin = px.y + px.h;
  const t = Math.max(0, Math.min(HEAD_TRIM_MAX, trim));
  const budget = Math.min(win.w / (2 * halfW * Math.max(1e-6, 1 - t * SIDE_TRIM)), win.h / Math.max(px.h * (1 - t), 1e-6));
  // A sampled outline is a pixel or two coarse; so much overlap is not a cut.
  const tolerance = 0.015 * win.w;
  const rows = profile && profile.length >= 4 ? profile : undefined;
  const faceFits = (scale: number): boolean => {
    const top = topAt(scale);
    const ex = centreX + ((x0 + x1) / 2 - cx) * scale;
    const ey = top + ((y0 + chin) / 2 - px.y) * scale;
    const rx = ((x1 - x0) / 2) * scale;
    const ry = ((chin - y0) / 2) * scale;
    if (ey - ry < win.y - tolerance || ey + ry > win.y + win.h + tolerance) return false;
    if (!rows) return ex - rx >= win.x - tolerance && ex + rx <= win.x + win.w + tolerance;
    for (let i = 0; i < rows.length; i++) {
      const y = win.y + ((i + 0.5) / rows.length) * win.h;
      const d = (y - ey) / ry;
      if (Math.abs(d) >= 1) continue;
      const hw = rx * Math.sqrt(1 - d * d);
      const [l, r] = rows[i]!;
      if (ex - hw < win.x + l * win.w - tolerance || ex + hw > win.x + r * win.w + tolerance) return false;
    }
    return true;
  };
  // Never smaller than the whole-head fit, even when that already touches.
  if (!faceFits(whole)) return { scale: whole, faceMax: whole };
  // The largest scale at which the face still clears the outline (the hair
  // is let go entirely); then the trim budget caps how far the fit goes.
  let lo = whole;
  let hi = whole * 4;
  if (faceFits(hi)) lo = hi;
  else {
    for (let i = 0; i < 28; i++) {
      const mid = (lo + hi) / 2;
      if (faceFits(mid)) lo = mid;
      else hi = mid;
    }
  }
  const faceMax = lo;
  return { scale: t > 0 ? Math.max(whole, Math.min(budget, faceMax)) : whole, faceMax };
}

/**
 * The crop that puts a measured head where the slot's rules want it.
 *
 * Always a `free` crop: the head may need to be smaller than the plain cover
 * fit allows (a close-up selfie) or sit where its photo cut it off (hair at
 * the top edge), and outside a cut-out there is only transparency. A lean of
 * the eye line is straightened about the head's own centre, so a tilted
 * selfie still sits upright on the body.
 */
export function cropForHead(
  head: HeadMeasure,
  picture: { w: number; h: number },
  slot: { w: number; h: number },
  fit?: HeadFit,
  /** The picture's other heads: when there are any, only this head is kept. */
  others: HeadMeasure[] = [],
): ImageCrop {
  const place = headPlacement(head, picture, slot, fit);
  const lean = Math.abs(head.roll) >= ROLL_MIN && Math.abs(head.roll) <= ROLL_MAX ? head.roll : 0;
  // Turning the picture by -lean levels the eyes.
  const rotation = lean ? Math.round(-lean * 2) / 2 : 0;

  // The head's centre, in source pixels and in slot pixels (relative to the
  // slot's centre, where the renderer turns the picture).
  const headSrc = {
    x: head.centerX * picture.w,
    y: (head.box.y + head.box.h / 2) * picture.h,
  };
  const headSlot = {
    x: place.centerX - slot.w / 2,
    y: place.y + place.h / 2 - slot.h / 2,
  };
  // renderer: slotPoint = R(rotation) · (src − crop) · k  →  crop = src − R(−rotation) · slotPoint / k
  const k = place.scale;
  const a = (-rotation * Math.PI) / 180;
  const ux = (headSlot.x * Math.cos(a) - headSlot.y * Math.sin(a)) / k;
  const uy = (headSlot.x * Math.sin(a) + headSlot.y * Math.cos(a)) / k;
  const centre = { x: headSrc.x - ux, y: headSrc.y - uy };

  // Zoom is relative to the plain cover fit of the (turned) slot, exactly as
  // `coverPhotoFrame` and `photoWindow` read it.
  const r = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(r));
  const sin = Math.abs(Math.sin(r));
  const turnedW = cos * slot.w + sin * slot.h;
  const turnedH = sin * slot.w + cos * slot.h;
  const cover = Math.max(turnedW / picture.w, turnedH / picture.h);
  const round = (v: number): number => Math.round(v * 10000) / 10000;
  return {
    cx: round(centre.x / picture.w),
    cy: round(centre.y / picture.h),
    zoom: round(Math.max(0.1, Math.min(8, k / cover))),
    ...(rotation ? { rotation } : {}),
    free: true,
    ...(others.some((o) => o !== head) ? { keep: keepBox(head, others) } : {}),
  };
}

/**
 * The part of a group cut-out that belongs to one head: its silhouette with a
 * margin for soft hair edges, trimmed back wherever a neighbour's head starts.
 */
export function keepBox(head: HeadMeasure, others: HeadMeasure[]): FractionBox {
  const b = head.box;
  const margin = 0.06;
  let x0 = b.x - b.w * margin;
  let x1 = b.x + b.w * (1 + margin);
  const y0 = b.y - b.h * margin;
  const y1 = b.y + b.h * (1 + margin);
  for (const o of others) {
    if (o === head) continue;
    const ob = o.box;
    // Only neighbours that share rows with this head can reach into its box.
    if (ob.y > y1 || ob.y + ob.h < y0) continue;
    if (ob.x + ob.w / 2 < head.centerX) x0 = Math.max(x0, Math.min(ob.x + ob.w, b.x));
    else x1 = Math.min(x1, Math.max(ob.x, b.x + b.w));
  }
  const r = (v: number): number => Math.round(v * 10000) / 10000;
  return { x: r(x0), y: r(y0), w: r(x1 - x0), h: r(y1 - y0) };
}

/**
 * Agent-readable description of Mini Me head placement (`get_capabilities`).
 * Authoring blueprints create face cut-out slots (`layer.faceCutout`) that use
 * the shop's head fit; this says what a slot's `headFit` means so an agent
 * reading or reviewing a template can explain and preserve it.
 */
export const HEAD_PLACEMENT_CAPABILITY = {
  applies: 'Image layers with a faceCutout effect (Mini Me). The cut-out comes back the size of the photo, transparent but for the head(s).',
  measurement: 'Server measures each head from the cut-out alpha (hair top to chin), matched to the detected face (centre line, eye-line lean). Touching heads (one connected shape under several faces) are split at the dip between their crowns before measuring, and a second head the face detector missed is offered on its own. Returned as `heads` by the cut-out routes.',
  height: 'Default (`fill: "height"`): the head, hair top to chin, is scaled to exactly the height of the whole layer box (or a pinned `area`), centred on the face\'s centre line and vertically per `align`. `size` scales from there (over 1 = bigger than the box); `trim` and the measured window are not used.',
  window: 'With `fill: "face"` the head is fitted into the slot\'s visible window: the slot mask minus layers drawn above it (body, collar, helmet rim), inside the product silhouette below it, ending at a bust mask\'s shoulder flare. Measured with the print renderer when the product loads, with its outline row by row.',
  fill: 'With `fill: "face"` the whole head always fits; then the face grows to fill the window: the hair may run past the outline (up to `trim` of the head\'s height at the crown, 0.8 of that off each side) while the face (detector box plus a little forehead and cheek, down to the chin) never crosses the measured outline. A head taller than the window keeps its chin on the window\'s bottom edge and loses its crown. A size over 1 grows the head only while the face still clears the outline.',
  element: {
    headFit: {
      fill: ['height', 'face'],
      size: [0.4, 1.6],
      align: ['top', 'center', 'bottom'],
      offsetX: [-0.5, 0.5],
      offsetY: [-0.5, 0.5],
      trim: [0, 1],
      off: 'boolean; keeps the older face framing',
      area: 'optional {x,y,w,h} fractions of the slot; absent = automatic window, {0,0,1,1} = whole slot',
    },
  },
  shopDefaults: 'Storefront settings headPlacement (flag), headFill (height/face), headSize (40–160 %), headAlign, headTrim (0–100 %, face fill only), cutoutAnimation (full/simple/off). Slot settings beat shop settings beat the built-in default (fill height, size 1, centre, trim 0.5).',
  crop: 'Placement writes a `free` ImageCrop (window may leave the picture, zoom 0.1–8; outside the cut-out is transparent) with `keep` = the chosen head of a group photo. The shopper may still move and zoom it. Preview and print render the same crop.',
  authoring: 'A blueprint makes a face slot with layer.faceCutout: true on a customer_photo layer (usually fit contain, maskAssetId + maskMode black-white): the faceCutout effect, the shopper may move, zoom and turn the head inside the mask, and the shop’s head-fit defaults apply. Per-slot headFit settings are Studio only (layer → Head placement); preserve existing headFit values when editing.',
} as const;
